import { useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, ShieldCheck, Crown, Sparkles } from "lucide-react";
import { toast } from "sonner";

const MODULE_LABELS = {
  dashboard: "Tableau de bord",
  orders: "Commandes",
  clients: "Clients",
  employees: "Salariés",
  sellers: "Vendeurs",
  subcontractors: "Sous-traitants",
  suppliers: "Fournisseurs",
  equipment: "Matériel",
  analytics: "Analyses & Rapports",
  commissions: "Commissions",
  finance: "Finance & Paiements",
  admin_tasks: "Administratif",
  field_requests: "Demandes chantier",
  employee_submissions: "Fiches salariés",
  settings: "Paramètres",
  billing: "Facturation",
};

const ACTION_LABELS = {
  delete_orders: "Supprimer des commandes",
  export_data: "Exporter des données (CSV/PDF)",
  edit_finance: "Modifier paiements / factures",
  manage_team: "Gérer l'équipe (inviter, supprimer)",
};

const LEVEL_LABELS = {
  none: "Aucun",
  read: "Lecture",
  write: "Écriture",
  admin: "Admin module",
};

export default function PermissionsEditorDialog({ open, member, onClose, onSaved }) {
  const { activeOrgId, permissionModules, permissionLevels, sensitiveActions } = useOrg();
  const [permissions, setPermissions] = useState({});
  const [actions, setActions] = useState({});
  const [employeeId, setEmployeeId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  useEffect(() => {
    if (!open || !member) return;
    // Initialise from member's current permissions, falling back to defaults if null
    const isAdmin = member.org_role === "admin";
    const defaultPerm = (permissionModules || []).reduce((acc, m) => {
      acc[m] = isAdmin ? "admin" : "none";
      return acc;
    }, {});
    const defaultAct = (sensitiveActions || []).reduce((acc, a) => {
      acc[a] = isAdmin;
      return acc;
    }, {});
    setPermissions(member.permissions ? { ...defaultPerm, ...member.permissions } : defaultPerm);
    setActions(member.actions ? { ...defaultAct, ...member.actions } : defaultAct);
    setEmployeeId(member.employee_id || "");
    setSelectedTemplate(member.role_template || "");
    api.getEmployees()
      .then((r) => setEmployees((r.data || []).filter((e) => !e.archived && !e.exit_date)))
      .catch(() => setEmployees([]));
    // Load templates list (P0/P1) — cached per dialog mount.
    api.getRoleTemplates?.()
      .then((r) => setTemplates(r.data?.templates || []))
      .catch(() => setTemplates([]));
  }, [open, member, permissionModules, sensitiveActions]);

  const applyTemplate = (tmplId) => {
    setSelectedTemplate(tmplId);
    if (!tmplId) return;
    const tmpl = templates.find((t) => t.id === tmplId);
    if (!tmpl) return;
    // Initialise une baseline complète (chaque module/action présent)
    const fullPerm = (permissionModules || []).reduce((acc, m) => {
      acc[m] = tmpl.permissions?.[m] || "none";
      return acc;
    }, {});
    const fullAct = (sensitiveActions || []).reduce((acc, a) => {
      acc[a] = Boolean(tmpl.actions?.[a]);
      return acc;
    }, {});
    setPermissions(fullPerm);
    setActions(fullAct);
    toast.success(`Template « ${tmpl.label || tmpl.id} » appliqué (modifiable avant enregistrement)`);
  };

  const handleLevelChange = (module, level) => {
    setPermissions((p) => ({ ...p, [module]: level }));
  };
  const handleActionToggle = (action, val) => {
    setActions((a) => ({ ...a, [action]: val }));
  };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const payload = {
        permissions,
        actions,
        employee_id: employeeId || "",
      };
      if (selectedTemplate) payload.role_template = selectedTemplate;
      await api.updateOrgMember(activeOrgId, member.user_id, payload);
      toast.success(`Permissions mises à jour pour ${member.email}`);
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Sauvegarde impossible"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!member) return null;
  const isOrgAdmin = member.org_role === "admin";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="permissions-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Permissions de {member.email}
          </DialogTitle>
          <DialogDescription>
            Définissez quels modules ce membre peut consulter ou modifier, et quelles actions sensibles il peut effectuer.
          </DialogDescription>
        </DialogHeader>

        {isOrgAdmin && (
          <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm flex items-center gap-2">
            <Crown className="h-4 w-4 text-amber-700 shrink-0" />
            <span>
              Ce membre est <strong>administrateur de l'organisation</strong> — il dispose de tous les droits.
              Les permissions ci-dessous ne s'appliqueront qu'après sa rétrogradation en membre simple.
            </span>
          </div>
        )}

        {/* Role templates — appliquer un preset en 1 clic */}
        {!isOrgAdmin && templates.length > 0 && (
          <div className="space-y-2" data-testid="permissions-templates">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-indigo-500" /> Appliquer un template
            </h3>
            <p className="text-xs text-muted-foreground">
              Pré-remplit les modules et actions. Tu peux affiner ensuite avant d'enregistrer.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {templates.filter(t => t.id !== "admin").map((t) => {
                const active = selectedTemplate === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => applyTemplate(t.id)}
                    className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-all duration-200 ease-out ${
                      active
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-700 border-zinc-200 hover:border-indigo-400 hover:bg-indigo-50"
                    }`}
                    data-testid={`template-${t.id}`}
                    title={t.description || ""}
                  >
                    {t.label || t.id}
                  </button>
                );
              })}
              {selectedTemplate && (
                <button
                  type="button"
                  onClick={() => setSelectedTemplate("")}
                  className="text-xs text-zinc-500 hover:text-zinc-900 underline-offset-2 hover:underline px-2 py-1"
                >
                  Aucun template
                </button>
              )}
            </div>
          </div>
        )}

        {/* Linked employee */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            Salarié lié
          </h3>
          <p className="text-xs text-muted-foreground">
            Lier ce compte à un salarié restreint la saisie d'heures et les demandes
            d'absences à ce seul salarié (RBAC strict).
          </p>
          <Select
            value={employeeId || "__none__"}
            onValueChange={(v) => setEmployeeId(v === "__none__" ? "" : v)}
          >
            <SelectTrigger className="h-9" data-testid="member-link-employee">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Aucun (utilisateur non lié)</SelectItem>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Modules */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Modules</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(permissionModules || []).map((module) => (
              <div
                key={module}
                className="flex items-center justify-between gap-3 p-2.5 rounded-md border bg-card hover:bg-muted/30"
                data-testid={`perm-row-${module}`}
              >
                <span className="text-sm font-medium">{MODULE_LABELS[module] || module}</span>
                <Select
                  value={permissions[module] || "none"}
                  onValueChange={(v) => handleLevelChange(module, v)}
                  disabled={isOrgAdmin}
                >
                  <SelectTrigger className="h-8 w-[140px]" data-testid={`perm-level-${module}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(permissionLevels || ["none", "read", "write", "admin"]).map((lvl) => (
                      <SelectItem key={lvl} value={lvl}>{LEVEL_LABELS[lvl] || lvl}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>

        {/* Sensitive actions */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Actions sensibles</h3>
          <div className="space-y-2">
            {(sensitiveActions || []).map((action) => (
              <div
                key={action}
                className="flex items-center justify-between gap-3 p-2.5 rounded-md border bg-card"
                data-testid={`action-row-${action}`}
              >
                <div>
                  <p className="text-sm font-medium">{ACTION_LABELS[action] || action}</p>
                </div>
                <Switch
                  checked={!!actions[action]}
                  onCheckedChange={(v) => handleActionToggle(action, v)}
                  disabled={isOrgAdmin}
                  data-testid={`action-switch-${action}`}
                />
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={handleSave}
            disabled={submitting}
            data-testid="save-permissions-btn"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
